/**
 * Where a citizen can move TO, today and next. One list, for every surface
 * that offers a destination.
 *
 * It lived as three `Set`s inside the Journey Builder's country widget — a
 * product decision (which corridors are configured) held in a component. The
 * citizen setup (Citizen first run) shows the destination too, and a second
 * copy is how "Portugal only" stays true in one place and quietly stops being
 * true in the other the day Spain opens.
 *
 * Opening a corridor is a content change first (TelePortal kits, CheckUp
 * persona, QultureUp worlds — see `BL-183`'s Spain gap): move its code from
 * `NEXT_DESTINATIONS` to `OPEN_DESTINATIONS` only when that content ships.
 */

import { DestinationNotOpenError } from '../errors/domain';
import { UNKNOWN_DESTINATION } from './countries';

/**
 * Fully configured: a citizen can build a journey here today. A literal list,
 * so what every open destination must declare is a `Record<OpenDestination, …>`
 * (its crisis lines, `crisis-lines.ts`) — opening one without it does not compile.
 */
export const OPEN_DESTINATIONS = ['PT'] as const;

/** A destination that is open today. */
export type OpenDestination = (typeof OPEN_DESTINATIONS)[number];

/** The next corridors — shown by name, disabled, as "coming soon". */
export const NEXT_DESTINATIONS: readonly string[] = ['ES', 'IT'];

/** The roadmap beyond them — shown with a "soon" badge, disabled. */
export const ROADMAP_DESTINATIONS: readonly string[] = [
  // EU member states (other than the open and next corridors)
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'RO',
  'SK',
  'SI',
  'SE',
  // Non-EU key markets
  'GB',
  'US',
  'CN',
  'AE',
];

/** The destination a self-built journey takes when only one is open. */
export function soleOpenDestination(): OpenDestination | null {
  return OPEN_DESTINATIONS.length === 1 ? OPEN_DESTINATIONS[0] : null;
}

/** The open destination a code names (any case, any padding), or null when it names none. */
export function openDestinationOf(code: string | null | undefined): OpenDestination | null {
  const named = code?.trim().toUpperCase();
  return OPEN_DESTINATIONS.find((open) => open === named) ?? null;
}

/**
 * Whether a journey or an invitation may go to this country TODAY — only an
 * OPEN destination. Everything else (the next corridors, the roadmap, any
 * other code) has no relocation kit, CheckUp persona or QultureUp world, so a
 * journey there is one the platform cannot serve; an invitation to one is
 * refused at every door (API schemas, the journey factory, the invitation
 * writes), not only hidden in a picker. Case-insensitive.
 */
export function isOpenDestination(code: string | null | undefined): boolean {
  return openDestinationOf(code) !== null;
}

/**
 * Which country's content — the per-country BRAIN configs (Care, QultureUp,
 * InOut) — a reader serves.
 *
 * - A journey to an open destination reads its own.
 * - A journey whose destination is not named yet (`UNKNOWN_DESTINATION` — a
 *   QultureUp-only citizen), and a reader with no journey at all (a catalog
 *   total, a company preview, the pre-warm), read the platform's only open
 *   destination.
 * - A journey to a NAMED destination that is not open has no content: it is
 *   a defect (no journey may go there), refused loudly — never served another
 *   country's.
 *
 * With several destinations open there is no "only" one: a reader with no
 * named destination must be given its country (sub-release B decides how).
 */
export function contentMarketFor(destination: string | null | undefined): string {
  const named = destination?.trim().toUpperCase();
  if (named && named !== UNKNOWN_DESTINATION) {
    if (isOpenDestination(named)) return named;
    throw new DestinationNotOpenError(named);
  }
  const sole = soleOpenDestination();
  if (sole) return sole;
  throw new Error(
    'No content country without a named destination: several destinations are open, so the reader must name one'
  );
}
